/**
 * useCustomerAuth — customer auth hook + storage helpers for /app.
 *
 * Iter 78 — real customer login.
 * Iter 80 (Feb 2026) — P0 fixes:
 *   1. Strict customer-vs-demo isolation: any login or refresh wipes the
 *      pilot/demo localStorage keys so /app can never silently render the
 *      "Owner Demo" pilot state.
 *   2. Silent session refresh: a response interceptor catches 401 on any
 *      `/api/customer/app/*` or `/api/customer/auth/me` call, hits the
 *      new `/api/customer/auth/refresh` endpoint (HttpOnly cookie based),
 *      then retries the original request once. Without this the 24h
 *      access token would still kick the user back to login on the rare
 *      cross-day edge case.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'powersure_customer_token';
const USER_KEY = 'powersure_customer_user';
const PILOT_TOKEN_KEY = 'powersure_pilot_token';

export function getCustomerToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function getCustomerUserCache() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
}

function wipePilotTokens() {
  try {
    localStorage.removeItem(PILOT_TOKEN_KEY);
    // Clear other demo-related local state that previously caused
    // "Hoi Owner Demo" to bleed into a real customer session.
    localStorage.removeItem('powersure_pilot_user');
    localStorage.removeItem('powersure_pilot_state');
  } catch { /* ignore */ }
}

function setSession(data) {
  if (!data) return;
  if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  // Real customer session active → demo/pilot state may never coexist.
  wipePilotTokens();
}

function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
}

export const customerApi = axios.create({ baseURL: API, withCredentials: true });

customerApi.interceptors.request.use((cfg) => {
  const t = getCustomerToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---- Iter 80: silent refresh on 401 ----
let _refreshInFlight = null;
async function refreshOnce() {
  if (!_refreshInFlight) {
    _refreshInFlight = axios.post(
      `${API}/customer/auth/refresh`,
      {},
      { withCredentials: true },
    ).then((res) => {
      setSession(res.data);
      return res.data;
    }).catch((err) => {
      // Refresh failed → wipe local cache so /app routes to /customer/login.
      clearSession();
      throw err;
    }).finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
}

customerApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !original || original._retry) throw error;
    const url = String(original.url || '');
    // Never retry on the auth endpoints themselves — that would loop.
    if (url.includes('/customer/auth/login')) throw error;
    if (url.includes('/customer/auth/refresh')) throw error;
    if (url.includes('/customer/auth/logout')) throw error;
    if (url.includes('/customer/auth/set-password')) throw error;
    if (url.includes('/customer/auth/forgot-password')) throw error;
    try {
      await refreshOnce();
    } catch {
      throw error;
    }
    original._retry = true;
    const newTok = getCustomerToken();
    if (newTok) {
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newTok}` };
    }
    return customerApi.request(original);
  },
);

export async function customerLogin(email, password) {
  const res = await customerApi.post('/customer/auth/login', { email, password });
  setSession(res.data);
  return res.data;
}

export async function customerSetPassword(token, password) {
  const res = await customerApi.post('/customer/auth/set-password', { token, password });
  setSession(res.data);
  return res.data;
}

export async function customerForgot(email) {
  const res = await customerApi.post('/customer/auth/forgot-password', { email });
  return res.data;
}

export async function customerLogout() {
  try { await customerApi.post('/customer/auth/logout'); } catch { /* ignore */ }
  clearSession();
  // Also wipe demo state so the page can never bleed back to "Owner Demo".
  wipePilotTokens();
}

export default function useCustomerAuth() {
  const [user, setUser] = useState(getCustomerUserCache());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const t = getCustomerToken();
      if (!t) { setUser(null); setLoading(false); return null; }
      const res = await customerApi.get('/customer/auth/me');
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      return res.data;
    } catch (e) {
      // After interceptor exhausted refresh attempts.
      clearSession();
      setUser(null);
      setError(e?.response?.data?.detail || 'Niet ingelogd');
      return null;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { user, loading, error, refresh, isCustomer: !!user };
}
